<template>
	<div class="statistics-chart-panel">
		<div class="statistics-chart-header">
			<div class="statistics-chart-title">
				<div class="list-title">
					{{ selectedCustomer ? selectedCustomer.name : $t('statistics.select_location_in_structure') }}
					<span v-if="start || stop" class="statistics-title-range">
						{{ formatDateRange(start, stop) }}
					</span>
				</div>
				<p class="statistics-muted">
					{{ $t('statistics.handled_vs_unhandled_flaws') }}
				</p>
			</div>
			<div class="statistics-legend">
				<span><i class="legend-box handled"></i> {{ $t('statistics.handled') }}</span>
				<span><i class="legend-box unhandled"></i> {{ $t('statistics.unhandled') }}</span>
			</div>
			<div class="statistics-chart-mode">
				<button
					type="button"
					:class="{ active: displayMode === 'count' }"
					@click="$emit('set-display-mode', 'count')"
				>
					{{ $t('statistics.count') }}
				</button>
				<button
					type="button"
					:class="{ active: displayMode === 'percent' }"
					@click="$emit('set-display-mode', 'percent')"
				>
					%
				</button>
			</div>
			<div class="statistics-sort-control">
				<div class="arrow sort-icon is-flex is-justify-content-flex-end is-align-items-center">
					<i :class="`fas fa-arrow-${sortDirection === 'desc' ? 'down' : 'up'}`" @click="$emit('toggle-sort-direction')"></i>
				</div>
				<div class="statistics-sort-select">
					<CustomSelect
						:value="selectedSortOption"
						:options="sortOptions"
						:placeholder="$t('statistics.sort_by')"
						:trackBy="'value'"
						@select="$emit('set-sort-by', $event)"
					/>
				</div>
			</div>
		</div>

		<ui-progress v-if="loading" />

		<div v-else-if="error" class="notification is-danger">
			{{ error }}
		</div>

		<div v-else-if="rows.length" class="building-chart">
			<div
				v-for="row in rows"
				:key="row.id"
				class="building-chart-row"
				:class="{
					active: selectedRow && selectedRow.id === row.id,
					'building-chart-row--department': row.type === 'department',
					'building-chart-row--building': row.type === 'building',
					'building-chart-row--child': row.level > 0,
					'building-chart-row--nested': row.level > 1
				}"
				:style="{ minHeight: getRowHeight(row) }"
			>
				<div
					class="building-chart-label"
					:title="row.name"
					:style="{ '--row-level': row.level }"
					@click="$emit('label-click', row)"
				>
					<i
						v-if="row.children.length"
						class="fas building-chart-expand"
						:class="isRowExpanded(row) ? 'fa-angle-down' : 'fa-angle-right'"
						@click.stop="$emit('toggle-row', row)"
					></i>
					<span v-else class="building-chart-expand-placeholder"></span>
					{{ row.name }}
				</div>
				<div
					class="building-chart-track"
					:style="{ height: getTrackHeight(row) }"
					@click="$emit('select-row', row)"
				>
					<div
						v-if="row.handledFlawCount > 0"
						class="building-chart-segment handled"
						:class="{ 'is-last': !row.unhandledFlawCount }"
						:style="{ width: getSegmentWidth(row.handledFlawCount, 'handled') }"
					>
						{{ getHandledLabel(row) }}
					</div>
					<div
						v-if="row.unhandledFlawCount > 0"
						class="building-chart-segment unhandled"
						:class="{ 'is-first': !row.handledFlawCount }"
						:style="{ width: getSegmentWidth(row.unhandledFlawCount, 'unhandled') }"
					>
						{{ getUnhandledLabel(row) }}
					</div>
				</div>
			</div>
		</div>

		<div v-else class="statistics-empty">
			{{ $t('statistics.no_statistics_for_selection') }}
		</div>
	</div>
</template>

<script>
import CustomSelect from '@/components/CustomSelect';

export default {
	name: 'StatisticsChartPanel',

	components: {
		CustomSelect
	},

	props: {
		displayMode: {
			type: String,
			required: true
		},
		error: {
			type: String,
			default: ''
		},
		formatDateRange: {
			type: Function,
			required: true
		},
		getHandledLabel: {
			type: Function,
			required: true
		},
		getRowHeight: {
			type: Function,
			required: true
		},
		getSegmentWidth: {
			type: Function,
			required: true
		},
		getTrackHeight: {
			type: Function,
			required: true
		},
		getUnhandledLabel: {
			type: Function,
			required: true
		},
		isRowExpanded: {
			type: Function,
			required: true
		},
		loading: {
			type: Boolean,
			default: false
		},
		rows: {
			type: Array,
			default: () => []
		},
		selectedCustomer: {
			type: Object,
			default: null
		},
		selectedRow: {
			type: Object,
			default: null
		},
		selectedSortOption: {
			type: Object,
			default: null
		},
		sortDirection: {
			type: String,
			required: true
		},
		sortOptions: {
			type: Array,
			default: () => []
		},
		start: {
			type: String,
			default: ''
		},
		stop: {
			type: String,
			default: ''
		}
	}
};
</script>
